import { ActionIcon, Badge, Box, Button, Group, Modal, Progress, Text, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconRefresh, IconRestore, IconTrash } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { DataTable } from "mantine-datatable";
import { PropsWithChildren } from "react";
import dayjs from "dayjs"
import { TooltipMultiline } from "@/Components/TooltipMultiline";
import { useDeleteApiEvaluateTestSetJobDelete, useGetApiEvaluateTestSetJobList, usePostApiEvaluateTestSetJobCreate, usePostApiEvaluateTestSetJobRequeue } from "@/API";
import { CreateEvaluateTestSetJobButton } from "./CreateEvaluateTestSetJob";
import { GroupTokenUsageByModel, SumTokenUsageDataWithModel } from "@/Helpers/TokenUsageHelper";

export function EvaluateTestSetJobManagerButton(props: { workspaceId: string, testSetId: string } & PropsWithChildren) {
    const queryClient = useQueryClient()
    const [opened, { open, close }] = useDisclosure(false);
    const list = useGetApiEvaluateTestSetJobList({ workspaceId: props.workspaceId, testSetId: props.testSetId })
    const create_Mutation = usePostApiEvaluateTestSetJobCreate()
    const requeue_Mutation = usePostApiEvaluateTestSetJobRequeue()
    const delete_Mutation = useDeleteApiEvaluateTestSetJobDelete()
    return <>
        <div onClick={open}>
            {props.children}
        </div>
        <Modal opened={opened} onClose={close} size="100%" title="測試評估工作管理">
            <Group mb="md">
                <CreateEvaluateTestSetJobButton workspaceId={props.workspaceId} onSubmit={async v => {
                    try {
                        v.testSetId = props.testSetId
                        await create_Mutation.mutateAsync({
                            params: {
                                workspaceId: props.workspaceId,
                            },
                            data: v
                        })
                        queryClient.invalidateQueries({ queryKey: list.queryKey })
                        notifications.show({ message: "新增成功" })
                    } catch (e) {
                        notifications.show({ message: "新增失敗", color: "red" })
                    }
                }}>
                    <Button>
                        創建工作
                    </Button>
                </CreateEvaluateTestSetJobButton>
                <ActionIcon variant="subtle" onClick={() => {
                    queryClient.invalidateQueries({ queryKey: list.queryKey })
                }}>
                    <IconRefresh style={{ width: '70%', height: '70%' }} />
                </ActionIcon>
            </Group>
            <DataTable
                withTableBorder
                minHeight={(list.data?.data.results?.length ?? 0) > 0 ? 0 : 150}
                noRecordsText="沒有資料"
                fetching={list.isFetching}
                columns={[
                    {
                        accessor: 'message', title: "訊息", render: (data) => <TooltipMultiline label={data.message}>
                            <Text w={160} truncate="end">
                                {data.message}
                            </Text>
                        </TooltipMultiline>
                    },
                    {
                        accessor: 'status', title: "工作狀態", render: (data) => {
                            let color = "gray";
                            if (data.status === "Completed") color = "green";
                            else if (data.status === "Processing") color = "blue";
                            else if (data.status === "Error") color = "red";
                            else if (data.status === "Waiting") color = "yellow";
                            return <Badge color={color}>{data.status}</Badge>;
                        }
                    },
                    {
                        accessor: 'chatTokenUsage',
                        title: "聊天 Token",
                        render: (data) => {
                            const allUsage = data.chatTokenUsage ?? [];
                            const totalUsage = SumTokenUsageDataWithModel(allUsage);
                            const groupedUsage = GroupTokenUsageByModel(allUsage);
                            const tooltipText = groupedUsage.map(x => `${x.model}: ${x.inputTokens} In / ${x.outputTokens} Out`).join('\n') || "無 Token 紀錄";
                            return (
                                <Tooltip label={tooltipText} multiline style={{ whiteSpace: "pre-line" }}>
                                    <Text size="sm">
                                        {totalUsage.inputTokens ?? 0} / {totalUsage.outputTokens ?? 0}
                                    </Text>
                                </Tooltip>
                            );
                        }
                    },
                    {
                        accessor: 'evaluationTokenUsage',
                        title: "評估 Token",
                        render: (data) => {
                            const allUsage = data.evaluationTokenUsage ?? [];
                            const totalUsage = SumTokenUsageDataWithModel(allUsage);
                            const groupedUsage = GroupTokenUsageByModel(allUsage);
                            const tooltipText = groupedUsage.map(x => `${x.model}: ${x.inputTokens} In / ${x.outputTokens} Out`).join('\n') || "無 Token 紀錄";
                            return (
                                <Tooltip label={tooltipText} multiline style={{ whiteSpace: "pre-line" }}>
                                    <Text size="sm">
                                        {totalUsage.inputTokens ?? 0} / {totalUsage.outputTokens ?? 0}
                                    </Text>
                                </Tooltip>
                            );
                        }
                    },
                    { accessor: 'createdTime', title: "創建時間", render: (data) => data.createdTime ? dayjs(data.createdTime).toDate().toLocaleString() : "" },
                    { accessor: 'updatedTime', title: "更新時間", render: (data) => data.updatedTime ? dayjs(data.updatedTime).toDate().toLocaleString() : "" },
                    {
                        accessor: 'progress', title: "進度", width: 100, render: (data) => {
                            var processed = data.processedCount ?? 0
                            var total = data.totalCount ?? 1
                            return <Tooltip label={`${processed}/${total}`}>
                                <Progress value={100 * processed / total} />
                            </Tooltip>
                        }
                    },
                    {
                        accessor: 'actions',
                        title: <Box mr={6}>操作</Box>,
                        textAlign: 'right',
                        render: (data) => (
                            <Group gap={4} justify="right" wrap="nowrap">
                                <ActionIcon
                                    size="sm"
                                    variant="subtle"
                                    onClick={() => {
                                        modals.openConfirmModal({
                                            title: '重新加入列隊',
                                            children: (
                                                <Text size="sm">
                                                    是否要將此工作重新加入列隊?
                                                </Text>
                                            ),
                                            labels: { confirm: '確認', cancel: '取消' },
                                            onConfirm: async () => {
                                                try {
                                                    if (data.id) {
                                                        await requeue_Mutation.mutateAsync({
                                                            params: {
                                                                workspaceId: props.workspaceId,
                                                                id: data.id
                                                            }
                                                        })
                                                        queryClient.invalidateQueries({ queryKey: list.queryKey })
                                                        notifications.show({ message: "操作成功" })
                                                    }
                                                } catch (e) {
                                                    notifications.show({ message: "操作失敗", color: "red" })
                                                }
                                            },
                                        });

                                    }}
                                >
                                    <IconRestore size={16} />
                                </ActionIcon>
                                <ActionIcon
                                    size="sm"
                                    variant="subtle"
                                    color="red"
                                    onClick={() => {
                                        modals.openContextModal({
                                            modal: "ConfirmCountDown",
                                            title: '刪除',
                                            innerProps: {
                                                countDownSecond: 0,
                                                children: (
                                                    <Text size="sm">
                                                        是否要刪除"{data.id}"?
                                                    </Text>
                                                ),
                                                labels: {
                                                    confirm: '刪除', cancel: '取消'
                                                },
                                                confirmProps: { color: 'red' },
                                                onConfirm: async () => {
                                                    try {
                                                        if (data.id) {
                                                            await delete_Mutation.mutateAsync({
                                                                params: {
                                                                    workspaceId: props.workspaceId,
                                                                    id: data.id
                                                                }
                                                            })
                                                            queryClient.invalidateQueries({ queryKey: list.queryKey })
                                                            notifications.show({ message: "刪除成功" })
                                                        }
                                                    } catch (e) {
                                                        notifications.show({ message: "刪除失敗", color: "red" })
                                                    }
                                                },
                                            },

                                        });
                                    }}
                                >
                                    <IconTrash size={16} />
                                </ActionIcon>
                            </Group>
                        ),
                    },
                ]}
                records={list.data?.data.results ?? []}
            />
        </Modal>
    </>
}
